import styled from 'styled-components';

export const EditorShell = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: #ffffff;
  overflow: hidden;
`;

export const EditorBody = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
`;

export const ToolRailWrapper = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 76px;
  flex: none;
  padding: 8px 0;
  border-right: 1px solid #f1f1f1;
  background: #fbfbfc;
  overflow-y: auto;
`;

export const ToolButton = styled.button<{ $active: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 10px 4px;
  font-size: 10px;
  line-height: 1.2;
  color: ${({ $active }) => ($active ? '#0b4f9e' : '#52525b')};
  background: ${({ $active }) => ($active ? '#eff6ff' : 'transparent')};
  border-left: 2px solid ${({ $active }) => ($active ? '#0b4f9e' : 'transparent')};
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: #f4f4f5;
  }
`;

export const PanelColumn = styled.aside`
  width: 288px;
  flex: none;
  border-right: 1px solid #f1f1f1;
  overflow-y: auto;
  overflow-x: hidden;
  background: #ffffff;
`;

/**
 * Obszar planszy. Krok AI kładzie się na nim nakładką zamiast go zastępować:
 * dzięki temu plansza zostaje zamontowana, a uchwyt eksportu w górnym pasku
 * nie znika razem z nią.
 */
export const StageArea = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  min-width: 0;
`;

export const InspectorColumn = styled.aside`
  width: 296px;
  flex: none;
  border-left: 1px solid #f1f1f1;
  overflow-y: auto;
  background: #ffffff;
`;

export const TopBarWrapper = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 12px;
  border-bottom: 1px solid #f1f1f1;
  background: #ffffff;
  flex-wrap: wrap;
`;

export const TopBarGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const FormatBadge = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;
  line-height: 1.25;
  white-space: nowrap;

  strong {
    font-size: 13px;
    color: #18181b;
    font-weight: 600;
  }

  span {
    font-size: 11px;
    color: #71717a;
  }
`;

/** Pasek podpowiedzi na dole edytora, poza obszarem roboczym planszy. */
export const EditorFooter = styled.footer`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 4px 14px;
  border-top: 1px solid #f1f1f1;
  background: #fbfbfc;
  font-size: 11px;
  color: #71717a;
  line-height: 1.6;
`;

export const EmptyPanelHint = styled.p`
  padding: 24px 16px;
  font-size: 12px;
  color: #71717a;
  line-height: 1.6;
  text-align: center;
`;

export const OptionGrid = styled.div<{ $columns?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns ?? 2}, 1fr);
  gap: 8px;
`;

export const OptionCard = styled.button<{ $active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px 8px;
  border-radius: 8px;
  border: 1px solid ${({ $active }) => ($active ? '#0b4f9e' : '#e4e4e7')};
  background: #ffffff;
  font-size: 11px;
  color: #3f3f46;
  transition: border-color 0.15s ease, background 0.15s ease;

  &:hover {
    border-color: #0b4f9e;
    background: #f8fafc;
  }
`;

export const TemplateCard = styled.button`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  border: 1px solid #e4e4e7;
  background: #ffffff;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #0b4f9e;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  }
`;

export const TemplateThumb = styled.div<{ $from: string; $to: string; $accent: string }>`
  position: relative;
  height: 72px;
  border-radius: 6px;
  background: linear-gradient(135deg, ${({ $from }) => $from}, ${({ $to }) => $to});
  overflow: hidden;

  &:after {
    content: '';
    position: absolute;
    left: 12px;
    right: 12px;
    bottom: 14px;
    height: 8px;
    border-radius: 4px;
    background: ${({ $accent }) => $accent};
  }

  &:before {
    content: '';
    position: absolute;
    left: 12px;
    right: 32px;
    top: 18px;
    height: 14px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.85);
  }
`;

export const LayerRow = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid ${({ $active }) => ($active ? '#0b4f9e' : 'transparent')};
  background: ${({ $active }) => ($active ? '#eff6ff' : 'transparent')};

  &:hover {
    background: #f4f4f5;
  }
`;

export const LayerName = styled.button`
  flex: 1;
  min-width: 0;
  text-align: left;
  font-size: 12px;
  color: #27272a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const AssetGrid = styled.div`
  display: grid;
  /* auto-fill z minimalną szerokością zamiast sztywnych 3 kolumn: przy wąskim
     panelu miniatury same się zwężają, zamiast wypychać poziomy pasek przewijania. */
  grid-template-columns: repeat(auto-fill, minmax(68px, 1fr));
  gap: 5px;
  min-width: 0;
`;

export const AssetTile = styled.div`
  position: relative;
  aspect-ratio: 1;
  min-width: 0;
`;

/** Akcje na kafelku biblioteki pokazują się dopiero pod kursorem. */
export const AssetActions = styled.div`
  position: absolute;
  inset: auto 3px 3px 3px;
  display: flex;
  gap: 3px;
  opacity: 0;
  transition: opacity 0.15s ease;

  ${AssetTile}:hover & {
    opacity: 1;
  }

  button {
    flex: 1;
    padding: 2px 0;
    font-size: 9px;
    line-height: 1.4;
    border-radius: 4px;
    color: #ffffff;
    background: rgba(24, 24, 27, 0.82);
  }

  button:hover {
    background: rgba(11, 79, 158, 0.92);
  }
`;

export const SavedCard = styled.button`
  display: flex;
  min-width: 0;
  gap: 10px;
  align-items: center;
  padding: 6px;
  border-radius: 8px;
  border: 1px solid #e4e4e7;
  background: #ffffff;
  text-align: left;
  width: 100%;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: #0b4f9e;
  }

  img {
    width: 56px;
    height: 56px;
    flex: none;
    object-fit: cover;
    border-radius: 6px;
    background: #f4f4f5;
  }
`;

export const SavedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

/** Znacznik na kafelku, np. numer wybranego zdjęcia albo rodzaj licencji. */
export const TileBadge = styled.span`
  position: absolute;
  top: 4px;
  left: 4px;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 9px;
  line-height: 1.6;
  color: #ffffff;
  background: rgba(24, 24, 27, 0.82);
  pointer-events: none;
`;

export const AssetThumb = styled.button`
  width: 100%;
  height: 100%;
  border-radius: 6px;
  border: 1px solid #e4e4e7;
  overflow: hidden;
  background: #f4f4f5;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &:hover {
    border-color: #0b4f9e;
  }
`;
